/* global navigator */
import { Set } from 'immutable';
import { bindDomEvents, unbindDomEvents } from 'app/utils/DomEvents';
import EventMapEmitter, { EventHandlerMap } from 'app/utils/EventMapEmitter';
import userAgent, { Browser, Platform } from 'app/utils/userAgent';
import { WebRTCFeatures } from 'app/services/rtc/constants';

export class MediaDevicesNotSupportedError extends Error {
  public constructor() {
    super('Browser doesn\'t support accessing media devices');

    Object.setPrototypeOf(this, MediaDevicesNotSupportedError.prototype);
  }
}

export class DisplayMediaNotSupportedError extends Error {
  public constructor() {
    super('Browser doesn\'t support screen sharing');

    Object.setPrototypeOf(this, MediaDevicesNotSupportedError.prototype);
  }
}

interface IMediaDevices {
  getSupportedFeatures(): Set<WebRTCFeatures>;
  getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
  getDisplayMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
  enumerateDevices(): Promise<MediaDeviceInfo[]>;
  bindEvents(eventMap: EventHandlerMap<MediaDevicesEventMap>): void;
  unbindEvents(eventMap: EventHandlerMap<MediaDevicesEventMap>): void;
  filterTrackConstraints(constraints: MediaTrackConstraints): MediaTrackConstraints;
  filterDisplayTrackConstraints(constraints: MediaTrackConstraints): MediaTrackConstraints;
}

class FullySupportedMediaInterface implements IMediaDevices {
  public constructor(protected readonly api: MediaDevices) {}

  public getSupportedFeatures(): Set<WebRTCFeatures> {
    return Set<WebRTCFeatures>().withMutations((features) => {
      features.add(WebRTCFeatures.UserMedia);

      if (getDisplayMediaApiGetter()) {
        features.add(WebRTCFeatures.DisplayMedia);
      }

      if (this.getSupportedConstraints().has('autoGainControl')) {
        features.add(WebRTCFeatures.AutoGain);
      }
    });
  }

  public async getUserMedia(constraints: MediaStreamConstraints) {
    return await this.api.getUserMedia(constraints);
  }

  public getDisplayMedia = async (constraints: MediaStreamConstraints) => {
    const getDisplayMedia = getDisplayMediaApiGetter();

    if (!getDisplayMedia) {
      throw new DisplayMediaNotSupportedError();
    }

    return getDisplayMedia(constraints);
  };

  public async enumerateDevices() {
    return await this.api.enumerateDevices();
  }

  public bindEvents(eventMap: EventHandlerMap<MediaDevicesEventMap>) {
    bindDomEvents(this.api, eventMap);
  }

  public unbindEvents(eventMap: EventHandlerMap<MediaDevicesEventMap>) {
    unbindDomEvents(this.api, eventMap);
  }

  /**
   * Filter contraints to allow only ones supported locally
   */
  public filterTrackConstraints(constraints: MediaTrackConstraints) {
    return this.filterSupportedConstraints(constraints, this.getSupportedConstraints());
  }

  public filterDisplayTrackConstraints(constraints: MediaTrackConstraints) {
    let supportedConstraints = this.getSupportedConstraints();

    const { browser, version } = userAgent();
    const [major, minor] = version;
    const isBrokenSafari = (
      browser === Browser.Safari &&
      major < 13 || major === 13 && minor <= 2
    );

    // Safari until 13.2 does not honor aspect ratio
    // when setting max constraints on getDisplayMedia
    if (isBrokenSafari) {
      supportedConstraints = supportedConstraints.delete('width')
                                                 .delete('height');
    }

    return this.filterSupportedConstraints(
      constraints,
      supportedConstraints,
    );
  }

  private filterSupportedConstraints(
    constraints: MediaTrackConstraints,
    supportedConstraints: Set<keyof MediaTrackSupportedConstraints>,
  ) {
    return supportedConstraints.reduce((acc, name) => {
      if (name in constraints) {
        return {
          ...acc,
          [name]: constraints[name],
        };
      }

      return acc;
    }, {});
  }

  private getSupportedConstraints(): Set<keyof MediaTrackSupportedConstraints> {
    if (this.api.getSupportedConstraints) {
      const supportedKeys = Object.keys(
        this.api.getSupportedConstraints(),
      ) as Array<keyof MediaTrackSupportedConstraints>;

      return Set(supportedKeys);
    } else {
      return Set();
    }
  }
}

class WrappedEventsMediaDevices extends FullySupportedMediaInterface {
  public readonly emitter: EventMapEmitter<MediaDevicesEventMap>;

  public constructor(api: MediaDevices) {
    super(api);
    this.emitter = new EventMapEmitter();
    this.api.ondevicechange = (event) => this.emitter.emit('devicechange', event);
  }

  public bindEvents(eventMap: EventHandlerMap<MediaDevicesEventMap>) {
    this.emitter.bindEvents(eventMap);
  }

  public unbindEvents(eventMap: EventHandlerMap<MediaDevicesEventMap>) {
    this.emitter.unbindEvents(eventMap);
  }
}

class ThrowingMediaDevices implements IMediaDevices {
  public async getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream> {
    throw new MediaDevicesNotSupportedError();
  }
  public async getDisplayMedia(constraints: MediaStreamConstraints): Promise<MediaStream> {
    throw new DisplayMediaNotSupportedError();
  }
  public async enumerateDevices(): Promise<MediaDeviceInfo[]> { return []; }
  public bindEvents() { throw new MediaDevicesNotSupportedError(); }
  public unbindEvents() { throw new MediaDevicesNotSupportedError(); }
  public getSupportedFeatures() { return Set<WebRTCFeatures>(); }
  public isAutoGainSupported() { return false; }
  public filterTrackConstraints() { return {}; }
  public filterDisplayTrackConstraints() { return {}; }
}

const hasMediaDevices = typeof navigator === 'object' && 'mediaDevices' in navigator;
const hasMediaDevicesWithEventHandlers = (
  hasMediaDevices &&
  typeof navigator.mediaDevices.addEventListener === 'function'
);

const getDisplayMediaApiGetter = () => {
  const { platform } = userAgent();

  if (platform === Platform.Mobile) return;

  return navigator.mediaDevices
                  ?.getDisplayMedia
                  ?.bind(navigator.mediaDevices);
};

const instance: IMediaDevices =
  hasMediaDevicesWithEventHandlers ? new FullySupportedMediaInterface(navigator.mediaDevices) :
  hasMediaDevices ? new WrappedEventsMediaDevices(navigator.mediaDevices) :
  new ThrowingMediaDevices();

export default instance;
